import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { unstable_useBlocker } from "react-router-dom";
import * as _ from "underscore";
import { List, LoadingDots } from "web-ui";
import { LegacyButton as Button } from "web-ui/legacy";

import type { TtsRequestParameters } from "../api/duolingoTts";
import { generateAnnotationsForExistingTts } from "../api/duolingoTts";
import {
  getVisemeMapping,
  getVisualizationData,
  hasSpeechAnnotations,
  updateAnnotationData,
} from "../api/transcription";
import type {
  PhonemeSpan,
  Span,
  VisualizationResponse,
} from "../api/transcription";
import styles from "../styles/SearchTTSResults.module.scss";
import { UrlParameterNames } from "../util/urlParameters";

import { Error as ErrorScreen } from "./Error";
import { NavigationConfirmationDrawer } from "./NavigationConfirmationDrawer";
import { PreviousVersionList } from "./PreviousVersionList";
import type { QueryResults } from "./Query";
import { RequestParametersDisplay } from "./RequestParametersDisplay";
import { SaveConfirmationDrawer } from "./SaveConfirmationDrawer";
import { VisemeContainer } from "./VisemeContainer";
import { Visualization } from "./Visualization";

const RESULTS_LITERAL = "Results";
const GENERATE_ANNOTATIONS_LITERAL = "Generate annotations";

export const SearchTTSResults = ({
  allowNewSearch,
  ttsDataResults,
}: {
  allowNewSearch: (shouldPreventNewSearch: boolean) => void;
  ttsDataResults: QueryResults;
}) => {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState<number>(0);
  const [selectedVersion, setSelectedVersion] = React.useState<
    number | "generated"
  >();
  const [selectedItem, setSelectedItem] =
    React.useState<TtsRequestParameters>();
  const [visualizationData, setVisualizationData] =
    React.useState<VisualizationResponse>();
  const [visemeMapping, setVisemeMapping] = React.useState<
    Record<string, string | undefined>
  >({});
  const [unchangedVisualizationData, setUnchangedVisualizationData] =
    React.useState<VisualizationResponse>();
  const [displayedVisualizationTtsHash, setDisplayedVisualizationTtsHash] =
    React.useState<string>();
  const [isGeneratingAnnotations, setIsGeneratingAnnotations] =
    React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [ttsRequestParametersOpen, setTTSRequestParametersOpen] =
    React.useState(true);
  const [confirmationDrawerToShow, setConfirmationDrawerToShow] =
    React.useState<"navigation" | "save">();
  const [desiredNewIndex, setDesiredNewIndex] = React.useState<number>();

  const [forceChildRerenderKey, setForceChildRerenderKey] = React.useState(0);
  const forceChildRerender = () =>
    setForceChildRerenderKey(currentKey => currentKey + 1);

  const audioRef = React.useRef<HTMLAudioElement>();
  const verifiedAnnotationPresenceIndicesRef = React.useRef<number[]>([]);

  const hasUnsavedChanges = !_.isEqual(
    unchangedVisualizationData,
    visualizationData,
  );

  // We use react-router to block on changes to other Routes in the app.
  // The use of the unstable API is necessary because react-router removed
  // the ability to block on navigation in v6, only recently bringing it back
  // with the unstable prefix (as v6.7.0). Per internal Stack Overflow, this
  // is okay to use for internal tools (https://stackoverflowteams.com/c/duolingo/questions/644).
  // Notably, this only works for changes to the Route, not for changes to the
  // the selected active item (handled otherwise below) or to the query (deliberately
  // not handled due to complexity vs. the low likelihood of a user accidentally
  // starting a new search)
  // Additional note: there is a bug in react-router where the hook in v6.8.1 erroneously
  // logs that more than one blocker is in use when it is not (https://github.com/remix-run/react-router/issues/10073)
  const navigationBlocker = unstable_useBlocker(hasUnsavedChanges);
  React.useEffect(() => {
    if (navigationBlocker.state !== "blocked") {
      return;
    }
    if (hasUnsavedChanges) {
      setConfirmationDrawerToShow("navigation");
      return;
    }
    navigationBlocker.reset();
    setConfirmationDrawerToShow(undefined);
  }, [hasUnsavedChanges, navigationBlocker]);

  React.useEffect(() => {
    allowNewSearch(!hasUnsavedChanges);
  }, [allowNewSearch, hasUnsavedChanges]);

  React.useEffect(() => {
    (async () => setVisemeMapping(await getVisemeMapping()))();
  }, []);

  React.useEffect(() => {
    setSelectedItemIndex(ttsDataResults.initialIndex ?? 0);
    verifiedAnnotationPresenceIndicesRef.current = [];
    setSelectedVersion(undefined);
  }, [ttsDataResults]);

  React.useEffect(() => {
    (async () => {
      setDesiredNewIndex(undefined);
      const selectedItemFromList = ttsDataResults.data[selectedItemIndex];
      if (
        !verifiedAnnotationPresenceIndicesRef.current.includes(
          selectedItemIndex,
        )
      ) {
        selectedItemFromList.has_speech_annotation = await hasSpeechAnnotations(
          selectedItemFromList.tts_hash,
        );
        verifiedAnnotationPresenceIndicesRef.current.push(selectedItemIndex);
      }
      setSelectedItem(selectedItemFromList);
    })();
  }, [ttsDataResults, selectedItemIndex, selectedVersion]);

  React.useEffect(() => {
    if (selectedItem?.has_speech_annotation) {
      (async () => {
        const visualizationResponse: VisualizationResponse =
          await getVisualizationData(selectedItem.tts_hash, selectedVersion);
        if (visualizationResponse) {
          setDisplayedVisualizationTtsHash(selectedItem.tts_hash);
          setVisualizationData(visualizationResponse);
          setUnchangedVisualizationData(visualizationResponse);
        }
      })();
    }

    if (selectedItem) {
      const parameters = new URLSearchParams(window.location.search);
      parameters.set(UrlParameterNames.ttsHash, selectedItem.tts_hash);

      const newURL = `${window.location.pathname}?${parameters}`;

      window.history.replaceState(null, "", newURL);
    }
  }, [selectedItem, selectedVersion]);

  const updatePhonemeSpans = (phonemeSpans: PhonemeSpan[]) => {
    setVisualizationData(oldVisualizationData =>
      oldVisualizationData
        ? {
            ...oldVisualizationData,
            annotation: {
              ...oldVisualizationData.annotation,
              phoneme_spans: phonemeSpans,
            },
          }
        : undefined,
    );
  };

  const updateWordSpans = (wordSpans: Span[]) => {
    setVisualizationData(oldVisualizationData =>
      oldVisualizationData
        ? {
            ...oldVisualizationData,
            annotation: {
              ...oldVisualizationData.annotation,
              spans: wordSpans,
            },
          }
        : undefined,
    );
  };

  const saveAnnotations = (forceSave = false) => {
    if (unchangedVisualizationData && visualizationData && selectedItem) {
      if (!forceSave && !hasUnsavedChanges) {
        setConfirmationDrawerToShow("save");
        return;
      }
      setIsSaving(true);
      updateAnnotationData(
        selectedItem.tts_hash,
        {
          phoneme_spans: visualizationData.annotation.phoneme_spans,
          spans: visualizationData.annotation.spans,
        },
        {
          baseVersion: selectedVersion,
          onSuccess: () => {
            setIsSaving(false);
            forceChildRerender();
          },
        },
      );
    }
  };

  return (
    <div className={styles["horizontal-wrap"]}>
      <div className={styles["query-list"]}>
        <h2>{RESULTS_LITERAL}</h2>
        <List
          items={ttsDataResults.data.flatMap(ttsData => ({
            description: `${ttsData.creator} • ${ttsData.speed}`,
            text: ttsData.text,
          }))}
          onChange={e => {
            if (
              e?.selectedIndices?.[0] === undefined ||
              ttsDataResults.data[e.selectedIndices[0]] === undefined
            ) {
              return;
            }
            const newItemResultsIndex = e.selectedIndices[0];
            if (hasUnsavedChanges) {
              setConfirmationDrawerToShow("navigation");
              // We have to set this as a state variable so that the drawer can access it
              setDesiredNewIndex(newItemResultsIndex);
              return;
            }
            setSelectedItemIndex(newItemResultsIndex);
          }}
          scrollable={true}
          selectedIndices={[selectedItemIndex]}
        />
        {selectedItem ? (
          <PreviousVersionList
            forceNewDataKey={forceChildRerenderKey}
            setVersion={version => setSelectedVersion(version)}
            ttsItem={selectedItem}
          />
        ) : null}
      </div>
      <div
        className={`${styles["vertical-wrap"]} ${
          ttsRequestParametersOpen ? styles["tts-request-params-open"] : ""
        }`}
      >
        {selectedItem ? (
          <>
            {selectedItem?.has_speech_annotation ? (
              displayedVisualizationTtsHash !== selectedItem.tts_hash ||
              visualizationData === undefined ? (
                <div className={styles["loading-dot-wrap"]}>
                  <LoadingDots type="button" />
                </div>
              ) : (
                <>
                  {visualizationData.annotation.phoneme_spans ? (
                    <VisemeContainer
                      audioRef={audioRef}
                      phonemeSpans={visualizationData.annotation.phoneme_spans}
                      ttsHash={selectedItem.tts_hash}
                      voice={selectedItem.voice.toLowerCase()}
                    />
                  ) : null}
                  <Visualization
                    audioRef={audioRef}
                    isSaving={isSaving}
                    onSaveAnnotations={() => saveAnnotations()}
                    updatePhonemeSpans={updatePhonemeSpans}
                    updateWordSpans={updateWordSpans}
                    visemeMapping={visemeMapping}
                    visualizationData={visualizationData}
                  />
                </>
              )
            ) : (
              <div className={styles["error-container"]}>
                <ErrorScreen subject="No phoneme spans for this TTS" />
                <Button
                  onClick={async () => {
                    setIsGeneratingAnnotations(true);
                    const {
                      language_id: languageId,
                      text,
                      tts_hash: ttsHash,
                      voice,
                    } = selectedItem;
                    try {
                      await generateAnnotationsForExistingTts(
                        languageId,
                        text,
                        ttsHash,
                        voice,
                      );
                      window.location.reload();
                    } catch (error) {
                      setIsGeneratingAnnotations(false);
                      alert(error);
                    }
                  }}
                  submitting={isGeneratingAnnotations}
                  variant="solid"
                >
                  {GENERATE_ANNOTATIONS_LITERAL}
                </Button>
              </div>
            )}
          </>
        ) : null}
      </div>
      {selectedItem ? (
        <div
          className={`${styles["request-parameters-display-container"]} ${
            ttsRequestParametersOpen ? styles["open"] : ""
          }`}
        >
          <div
            className={styles["toggle-request-parameters-display"]}
            onClick={() => setTTSRequestParametersOpen(current => !current)}
          >
            <FontAwesomeIcon
              icon={ttsRequestParametersOpen ? faAngleRight : faAngleLeft}
            />
          </div>
          <RequestParametersDisplay
            audioRef={audioRef}
            showAudioButton={!selectedItem.has_speech_annotation}
            ttsData={selectedItem}
          />
        </div>
      ) : null}
      <SaveConfirmationDrawer
        isVisible={confirmationDrawerToShow === "save"}
        onDismiss={() => setConfirmationDrawerToShow(undefined)}
        onSaveAnyways={() => saveAnnotations(true)}
      />
      <NavigationConfirmationDrawer
        isVisible={confirmationDrawerToShow === "navigation"}
        onDismiss={() => {
          setConfirmationDrawerToShow(undefined);
          setDesiredNewIndex(undefined);
          if (navigationBlocker.state === "blocked") {
            navigationBlocker?.reset?.();
          }
        }}
        onNavigateAnyways={() => {
          if (navigationBlocker.state === "blocked") {
            navigationBlocker.proceed();
            return;
          }
          if (desiredNewIndex === undefined) {
            alert("Attempted to navigate to undefined new index");
            throw Error("Attempted to navigate to undefined new index");
          }
          setSelectedItemIndex(desiredNewIndex);
        }}
      />
    </div>
  );
};
